import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import {
  DataFormActionsComponent,
  DataFormCardComponent,
  DataFormGridComponent,
  ErrorBlockComponent,
} from '@clinic-platform/ui-components';
import { GlobalSettingsDto } from '../../global-settings/data-access/global-settings.models';
import { GlobalSettingsService } from '../../global-settings/data-access/global-settings.service';
import { SettingsRequestStateBase } from './settings-request-state.base';
import { SETTINGS_TAB_FORM_STYLES } from './settings-tab-form.styles';

@Component({
  selector: 'app-global-settings-tab',
  standalone: true,
  imports: [
    DataFormActionsComponent,
    DataFormCardComponent,
    DataFormGridComponent,
    ErrorBlockComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  template: `
    <lib-data-form-card>
      <h3>Global Settings</h3>
      <p class="settings-tab__hint">
        System-wide platform limits and switches. This tab is visible only for system administrators.
      </p>

      <form [formGroup]="form" (ngSubmit)="save()">
        <lib-data-form-grid>
          <label>
            Max upload file size (MB)
            <input type="number" formControlName="maxUploadFileSizeMb" min="1" max="10240" />
          </label>

          <label>
            Max attachments per entity
            <input type="number" formControlName="maxAttachmentsPerEntity" min="1" max="100" />
          </label>

          <label class="settings-tab__checkbox">
            <input type="checkbox" formControlName="maintenanceMode" />
            Maintenance mode
          </label>
        </lib-data-form-grid>

        <lib-data-form-actions>
          <button type="submit" [disabled]="form.invalid || !canSaveSettings">
            {{ isSaving ? 'Saving...' : 'Save global settings' }}
          </button>
        </lib-data-form-actions>
      </form>

      @if (sourceText) {
        <div class="settings-tab__meta">Source: {{ sourceText }}</div>
      }

      @if (errorText) {
        <lib-error-block variant="error" [message]="(errorText | t) ?? ''" />
      }

      @if (successText) {
        <lib-error-block variant="success" [message]="successText" />
      }
    </lib-data-form-card>
  `,
  styles: [SETTINGS_TAB_FORM_STYLES],
})
export class GlobalSettingsTabComponent extends SettingsRequestStateBase implements OnInit, OnDestroy
{
  private readonly service = inject(GlobalSettingsService);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly form = this.formBuilder.group({
    maxUploadFileSizeMb: this.formBuilder.nonNullable.control(25, [
      Validators.required,
      Validators.min(1),
      Validators.max(10240),
    ]),
    maxAttachmentsPerEntity: this.formBuilder.nonNullable.control(10, [
      Validators.required,
      Validators.min(1),
      Validators.max(100),
    ]),
    maintenanceMode: this.formBuilder.nonNullable.control(false),
  });

  protected sourceText: 'default' | 'stored' | null = null;

  public ngOnInit(): void
  {
    this.load();
  }

  protected save(): void
  {
    if (this.form.invalid || !this.canSaveSettings) {
      return;
    }

    this.beginSaving();

    const value = this.form.getRawValue();

    this.currentSettingsRequest(this.service.updateSettings({
      maxUploadFileSizeMb: value.maxUploadFileSizeMb,
      maxAttachmentsPerEntity: value.maxAttachmentsPerEntity,
      maintenanceMode: value.maintenanceMode,
    })).subscribe({
      next: (settings: GlobalSettingsDto) => {
        this.sourceText = settings.source;
        this.setSuccess('Global settings saved');
        this.finishSaving();
      },
      error: (error: unknown) => {
        this.setHttpError(error);
        this.finishSaving();
      },
    });
  }

  private load(): void
  {
    this.sourceText = null;

    this.loadSettings(this.service.getSettings(), this.form).subscribe({
      next: (settings: GlobalSettingsDto) => {
        this.form.patchValue({
          maxUploadFileSizeMb: settings.maxUploadFileSizeMb,
          maxAttachmentsPerEntity: settings.maxAttachmentsPerEntity,
          maintenanceMode: settings.maintenanceMode,
        });
        this.sourceText = settings.source;
      },
      error: (error: unknown) => {
        this.setHttpError(error);
      },
    });
  }

  public ngOnDestroy(): void {
    this.disposeSettings();
  }
}
